/**
 * Core-100 R4 — CORE-BP consumer contract: an accepted suggestion becomes ORDINARY work.
 *
 * CORE-BP's downstream consumer is "CORE-Y and scheduling (accepted work)". There are two paths on which a
 * human accepts a suggestion, and on both the accepted family must arrive as a normal job carrying the
 * catalogue's identity and the catalogue's duration — the duration scheduling books against:
 *
 *   · at creation (CreateTicket) — the accepted catalogue job joins the selected jobs, and the job list
 *     OptiPlace schedules from, through `acceptedSuggestionCreationJob` below;
 *   · on an existing ticket (AddonSuggestionProvider) — `addCatalogJobsToTicket` writes `jobs` +
 *     `ticket_jobs` and recomputes the ticket's predicted minutes.
 *
 * The creation path's mapping lived inline in CreateTicket's JSX, where nothing could test it. It is the same
 * mapping, moved here unchanged so the contract can be asserted (tests/unit/core100/r4BpAcceptedWorkContract
 * and the real-stack proof coreR4G6RuntimeProof).
 */

/** The `jobs_catalog` columns the creation path reads. */
export interface AcceptedCatalogJobRow {
  id: string;
  label: string;
  category_key: string;
  sub_menu: string | null;
  minutes_default: number;
  skill_level: string | number | null;
  proficiency_category?: string | null;
  requires?: unknown;
}

/** The selected-job shape CreateTicket schedules from. */
export interface AcceptedCreationJob {
  tempJobId: string;
  category: string;
  submenu: string | null;
  name: string;
  hours: number;
  bay: string;
  skill: number;
  proficiency_category?: string;
  catalogJobId: string;
}

export function acceptedSuggestionCreationJob(catalogJob: AcceptedCatalogJobRow, tempJobId: string): AcceptedCreationJob {
  const requiresData = (catalogJob.requires ?? null) as Record<string, unknown> | null;
  return {
    tempJobId,
    category: catalogJob.category_key,
    submenu: catalogJob.sub_menu,
    name: catalogJob.label,
    hours: catalogJob.minutes_default / 60,
    bay: (requiresData?.bay as string) || 'Lift',
    skill: parseInt(String(catalogJob.skill_level)) || 2,
    proficiency_category: catalogJob.proficiency_category || undefined,
    catalogJobId: catalogJob.id,
  };
}
